import React from 'react';
import {
  ButtonPriority,
  HoverStyles,
  PaddingModes,
  StatesButton,
  StatesButtonStates,
  ThreeDotsLoader,
} from '@wix/wix-ui-tpa/cssVars';
import classNames from 'classnames';
import {classes} from './AddToCartButton.st.css';
import productPageButtonsContainerStyles from '../ProductPageButtonsContainer.scss';
import {ProvidedGlobalProps, withGlobalProps} from '../../../providers/globalPropsProvider';
import {
  IProvidedTranslationProps,
  withTranslations,
} from '@wix/wixstores-client-common-components/dist/es/src/outOfIframes/translations';
import {Announcer} from '@wix/wixstores-client-core/dist/es/src/a11y/announcer';
import {AddToCartState} from '@wix/wixstores-client-storefront-sdk/dist/es/src/services/AddToCartService/constants';
import {AddToCartActionStatus} from '../../../constants';

export interface AddToCartButtonProps extends ProvidedGlobalProps, IProvidedTranslationProps {
  productInStock: boolean;
  handleAddToCart: any;
}

export const AddToCartButtonDataHook = {Button: 'add-to-cart', LoadingIndicator: 'add-to-cart-loading-indicator'};

class AddToCartButtonComponent extends React.Component<AddToCartButtonProps> {
  public AddToCartButtonRef = React.createRef<StatesButton>();
  private a11yAnnouncer: Announcer;

  public componentDidMount(): void {
    this.a11yAnnouncer = new Announcer('product-page-announcer');
  }

  public componentWillUnmount(): void {
    this.a11yAnnouncer.cleanup();
  }

  public componentDidUpdate(prevProps: Readonly<AddToCartButtonProps>): void {
    const {shouldFocusAddToCartButton, addedToCartStatus, userInputs} = this.props.globals;
    if (
      shouldFocusAddToCartButton !== false &&
      shouldFocusAddToCartButton !== prevProps.globals.shouldFocusAddToCartButton
    ) {
      this.AddToCartButtonRef.current.focus();
    }

    if (
      addedToCartStatus !== prevProps.globals.addedToCartStatus &&
      addedToCartStatus === AddToCartActionStatus.SUCCESSFUL
    ) {
      if (userInputs.quantity[0] > 1) {
        this.a11yAnnouncer.announce(
          this.props.t('SR_ANNOUNCE_ADDED_TO_CART_PLURAL', {
            productsNumber: this.props.globals.userInputs.quantity[0],
          })
        );
      } else {
        this.a11yAnnouncer.announce(
          this.props.t('SR_ANNOUNCE_ADDED_TO_CART_SINGULAR', {productName: this.props.globals.product.name})
        );
      }
    }
  }

  private getAddToCartState() {
    const {t} = this.props;
    const {addToCartState, labelOverrides} = this.props.globals;
    let disabled = false;
    let caption = t('ADD_TO_CART_BUTTON');

    if (addToCartState === AddToCartState.PRE_ORDER) {
      caption = t('productPage.preOrder.button');
    }

    if (addToCartState === AddToCartState.DISABLED) {
      caption = t('ADD_TO_CART_BUTTON_PRICE_ZERO');
      disabled = true;
    }

    if (addToCartState === AddToCartState.OUT_OF_STOCK) {
      caption = t('PRODUCT_OUT_OF_STOCK_BUTTON');
      disabled = true;
    }

    if (addToCartState === AddToCartState.UNAVAILABLE) {
      caption = t('productPage.unavailable.button');
      disabled = true;
    }

    if (labelOverrides.cta.addToCart) {
      caption = labelOverrides.cta.addToCart;
    }

    return {disabled, caption};
  }

  private getButtonState() {
    const {addedToCartStatus, shouldShowAddToCartSuccessAnimation} = this.props.globals;
    /* istanbul ignore next */
    if (shouldShowAddToCartSuccessAnimation && addedToCartStatus === AddToCartActionStatus.SUCCESSFUL) {
      return StatesButtonStates.SUCCESS;
    } else {
      return addedToCartStatus === AddToCartActionStatus.IN_PROGRESS
        ? StatesButtonStates.IN_PROGRESS
        : StatesButtonStates.IDLE;
    }
  }

  public render(): JSX.Element {
    const {isMobile, resetAddedToCartStatus, shouldShowWishlistButton} = this.props.globals;
    const {caption, disabled} = this.getAddToCartState();
    const shouldShowWishlist = shouldShowWishlistButton && !isMobile;
    const buttonClasses = classNames(classes.addToCartButton, {
      [productPageButtonsContainerStyles.primaryButton]: shouldShowWishlist,
    });
    const buttonState = this.getButtonState();

    return (
      <StatesButton
        state={buttonState}
        ref={this.AddToCartButtonRef as any}
        idleContent={caption}
        inProgressContent={
          <div className={classes.threeDotsButtonWrapper}>
            <ThreeDotsLoader className={classes.threeDotsButton} data-hook={AddToCartButtonDataHook.LoadingIndicator} />
          </div>
        }
        onNotificationEnd={() => resetAddedToCartStatus()}
        onClick={this.props.handleAddToCart}
        disabled={disabled}
        className={buttonClasses}
        fullWidth={!shouldShowWishlist}
        data-hook={AddToCartButtonDataHook.Button}
        hoverStyle={HoverStyles.underline}
        priority={ButtonPriority.primary}
        paddingMode={PaddingModes.explicitPaddings}
      />
    );
  }
}

export const AddToCartButton = withGlobalProps(withTranslations('globals.texts')(AddToCartButtonComponent));
